import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Loader2, Sparkles, X, Zap, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import CinematicBackdrop from '../cinematic/CinematicBackdrop';
import NumberTicker from '../cinematic/NumberTicker';
import MagneticButton from '../cinematic/MagneticButton';
import SplitText from '../cinematic/SplitText';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reference pricing for the "vs the competition" table (verified Feb 2026)
const COMPETITORS = [
  { name: 'Higgsfield', price: '$9 / 150 cr', perDollar: '17 cr/$', monthlyReset: true },
  { name: 'Runway',     price: '$15 / 625 cr', perDollar: '42 cr/$', monthlyReset: true },
  { name: 'Pika',       price: '$10 / 700 cr', perDollar: '70 cr/$', monthlyReset: true },
  { name: 'Leonardo',   price: '$10 / 8500 fast', perDollar: 'monthly', monthlyReset: true },
];

const PERKS = [
  '150 AI models — DALL·E 3, Midjourney v7, FLUX, Sora 2, Kling, Runway, Veo 2, Hunyuan…',
  'Director-mode cinematic camera presets',
  'Character consistency across multiple generations',
  'Credits never expire — buy once, use forever',
  'No subscription, no auto-renew, no surprises',
  'Image · Video · Upscale · Inpaint · Sketch · Image-to-Video',
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState('');

  useEffect(() => {
    axios.get(`${API}/pricing/packages`).then((r) => setPackages(r.data || [])).catch(() => {});
  }, []);

  const handleCheckout = (packageId) => {
    if (!user) { navigate('/login'); return; }
    setCheckoutLoading(packageId);
    navigate(`/checkout?package=${packageId}`);
  };

  return (
    <div className="min-h-screen text-white cinematic-grain" data-testid="pricing-page">
      <CinematicBackdrop intensity="normal" />
      <Navbar />

      <div className="max-w-[1280px] mx-auto px-6 sm:px-10 pt-24 pb-24 relative z-10">
        {/* Header */}
        <div className="relative text-center mb-16 cine-fade-up">
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#A3FF00] mb-4">Pricing — 3× cheaper than Higgsfield</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-[88px] font-black tracking-[-0.02em] uppercase text-white mb-5 leading-[0.95]" data-testid="pricing-heading">
            <SplitText text="More credits." className="block" stagger={0.04} duration={0.7} />
            <span className="italic bg-gradient-to-r from-[#FF6B4A] via-[#E040A0] to-[#FFD93D] bg-clip-text text-transparent">
              <SplitText text="Less money." stagger={0.05} duration={0.8} delay={0.4} />
            </span>
          </h1>
          <p className="text-base text-white/60 max-w-xl mx-auto">
            Buy once, use forever. <span className="text-white">Credits never expire.</span> No subscription. <span className="text-[#FFD93D] font-semibold">50 free credits</span> on signup.
          </p>
        </div>

        {/* Packages */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-20 relative" data-testid="pricing-packages">
          {packages.map((pkg, i) => {
            const isPopular = pkg.id === 'pro';
            const isBestValue = pkg.id === 'ultimate';
            // 4th tier id is 'enterprise' on the backend but UI labels it "Studio". Alias testid to match label.
            const testKey = pkg.id === 'enterprise' ? 'studio' : pkg.id;
            return (
              <div
                key={pkg.id}
                className={`tilt-card relative p-7 rounded-3xl border transition-colors flex flex-col cine-fade-up cine-fade-up--d${Math.min(4, i + 1)} ${
                  isPopular
                    ? 'bg-gradient-to-br from-[#FF6B4A]/15 to-[#E040A0]/15 border-[#FF6B4A]/40 shadow-[0_8px_40px_rgba(255,107,74,0.2)]'
                    : isBestValue
                    ? 'bg-gradient-to-br from-[#FFD93D]/12 to-[#FF6B4A]/8 border-[#FFD93D]/40'
                    : 'bg-white/[0.03] border-white/[0.08] hover:border-white/[0.15]'
                }`}
                data-testid={`pricing-card-${testKey}`}
              >
                <div className="tilt-card__sheen" />
                {isPopular && (
                  <div className="absolute -top-3 left-5 px-3 py-1 rounded-full bg-gradient-to-r from-[#FF6B4A] to-[#E040A0] text-white text-[9px] font-bold tracking-[0.2em] uppercase flex items-center gap-1">
                    <Zap className="w-2.5 h-2.5" /> Most Popular
                  </div>
                )}
                {isBestValue && (
                  <div className="absolute -top-3 left-5 px-3 py-1 rounded-full bg-[#FFD93D] text-[#0A0A0F] text-[9px] font-bold tracking-[0.2em] uppercase flex items-center gap-1">
                    <Crown className="w-2.5 h-2.5" /> Best Value
                  </div>
                )}

                <div className="mb-5">
                  <div className="text-[10px] font-bold tracking-[0.2em] text-white/50 uppercase mb-2">{pkg.name}</div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-display text-5xl font-black tracking-tight text-white">
                      <NumberTicker value={pkg.price} prefix="$" decimals={pkg.price % 1 === 0 ? 0 : 2} duration={1.4} />
                    </span>
                    <span className="text-xs text-white/40">one-time</span>
                  </div>
                  <div className="text-xs font-bold text-[#FF6B4A] mt-1 tracking-wider uppercase">
                    <NumberTicker value={pkg.credits} duration={1.6} /> credits
                  </div>
                  <div className="text-[10px] text-[#34B76F] mt-0.5 font-semibold">
                    <NumberTicker value={Math.round(pkg.credits / pkg.price)} duration={1.4} /> cr per dollar
                  </div>
                </div>

                <div className="space-y-2 mb-6 flex-1">
                  {pkg.features.map((feat) => (
                    <div key={feat} className="flex items-start gap-2 text-xs">
                      <Check className="w-3.5 h-3.5 text-[#34B76F] shrink-0 mt-0.5" />
                      <span className="text-white/75 leading-snug">{feat}</span>
                    </div>
                  ))}
                </div>

                <MagneticButton strength={0.25} radius={80} className="block w-full">
                  <Button
                    onClick={() => handleCheckout(pkg.id)}
                    disabled={checkoutLoading === pkg.id}
                    className={`w-full py-5 rounded-full font-bold tracking-[0.15em] uppercase text-[11px] transition-colors ${
                      isPopular
                        ? 'bg-gradient-to-r from-[#FF6B4A] to-[#E040A0] text-white hover:from-[#FF8E72] hover:to-[#FF6B9D] shadow-[0_8px_24px_rgba(255,107,74,0.3)]'
                        : isBestValue
                        ? 'bg-[#FFD93D] text-[#0A0A0F] hover:bg-[#FFE066]'
                        : 'bg-white/[0.04] text-white border border-white/[0.1] hover:bg-white/[0.08]'
                    }`}
                    data-testid={`checkout-btn-${testKey}`}
                  >
                    {checkoutLoading === pkg.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <>{user ? 'Purchase' : 'Sign In to Buy'} <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></>}
                  </Button>
                </MagneticButton>
              </div>
            );
          })}
        </div>

        {/* Higgsfield comparison */}
        <div className="relative mb-20" data-testid="vs-higgsfield">
          <div className="text-center mb-8">
            <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#FF6B4A] mb-2">vs The Competition</p>
            <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tight uppercase text-white">How we beat Higgsfield, Runway, Pika.</h2>
            <p className="text-sm text-white/55 mt-2 max-w-md mx-auto">Same generation quality. 3× more credits per dollar. Credits never expire.</p>
          </div>
          <div className="overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.02]">
            <div className="grid grid-cols-4 px-5 py-3 border-b border-white/[0.06] text-[10px] font-bold tracking-[0.2em] uppercase text-white/40">
              <div>Platform</div><div>Entry Price</div><div className="text-center">Credits / $</div><div className="text-right">Credits expire?</div>
            </div>
            <div className="grid grid-cols-4 px-5 py-4 bg-[#FF6B4A]/5 border-b border-white/[0.04]">
              <div className="flex items-center gap-2">
                <span className="font-display text-lg font-black text-white">DIS2ORT</span>
                <span className="px-2 py-0.5 rounded-full bg-[#34B76F]/20 text-[#34B76F] text-[9px] font-bold tracking-wider uppercase">You&apos;re here</span>
              </div>
              <div className="text-sm text-white">$4.99 / 250 cr</div>
              <div className="text-center text-sm font-bold text-[#34B76F]">50 cr/$</div>
              <div className="text-right text-xs text-[#34B76F] font-bold flex items-center justify-end gap-1"><Check className="w-3.5 h-3.5" /> Never</div>
            </div>
            {COMPETITORS.map((c) => (
              <div key={c.name} className="grid grid-cols-4 px-5 py-4 border-b border-white/[0.04] last:border-0">
                <div className="text-sm text-white/70">{c.name}</div>
                <div className="text-sm text-white/60">{c.price}</div>
                <div className="text-center text-sm text-white/60">{c.perDollar}</div>
                <div className="text-right text-xs text-red-400/80 flex items-center justify-end gap-1"><X className="w-3.5 h-3.5" /> Monthly reset</div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-white/30 mt-3 text-center">Reference pricing as of Feb 2026. Competitors charge per month — DIS2ORT is one-time.</p>
        </div>

        {/* Perks */}
        <div className="relative">
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#FF6B4A] text-center mb-6">Every plan includes</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {PERKS.map((p) => (
              <div key={p} className="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.05] flex items-start gap-3">
                <Sparkles className="w-4 h-4 text-[#FF6B4A] shrink-0 mt-0.5" />
                <div className="text-xs text-white/80 leading-relaxed">{p}</div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ teaser */}
        <div className="relative mt-16 text-center">
          <p className="text-xs text-white/40">Questions? <a href="/faq" className="text-[#FF6B4A] hover:text-[#FFD93D] underline-offset-2 hover:underline">Read the FAQ</a></p>
        </div>
      </div>
    </div>
  );
}
